/**
 * Figure de fonction dans la vidéo : mêmes axes et mêmes couleurs que dans l'app (Mafs), mais pilotée par
 * l'image courante : la courbe se trace, les paramètres glissent, le point suit la courbe.
 */
import "mafs/core.css";
import { Coordinates, Mafs, Plot, Point, Text } from "mafs";
import { useMemo } from "react";
import { compileExpr, FPS, paramsAt, type FigureSpec, type FigureState } from "@jange/shared";
import { fmt } from "@jange/lesson-player";
import type { Theme } from "../styles.js";

const axisStep = (span: number) => [1, 2, 5, 10, 20, 50, 100, 200].find((s) => span / s <= 9) ?? 500;
const DRAW_FRAMES = Math.round(2 * FPS);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));

export function Figure({ spec, state, frame, width, height, theme, label }: { spec: FigureSpec; state: FigureState; frame: number; width: number; height: number; theme: Theme; label?: string }) {
  const names = spec.sliders.map((s) => s.name);
  const fns = useMemo(() => spec.curves.map((c) => { const r = compileExpr(c.expr, ["x", ...names]); return r.ok ? r.expr.fn : () => NaN; }), [spec]); // eslint-disable-line react-hooks/exhaustive-deps
  const params = paramsAt(state, frame);
  const evalAt = (i: number, x: number) => { const y = fns[i]?.({ ...params, x }); return Number.isFinite(y) ? (y as number) : NaN; };
  const [xMin, xMax] = spec.view.x;
  const xStep = axisStep(xMax - xMin), yStep = axisStep(spec.view.y[1] - spec.view.y[0]);
  const draw = state.drawAt === null ? 0 : easeOut(clamp01((frame - state.drawAt) / DRAW_FRAMES));
  const appear = state.shownAt === null ? 0 : clamp01((frame - state.shownAt) / (0.4 * FPS));
  const colors: Record<string, string> = { teal: theme.curve, amber: theme.amber, ink: theme.text, muted: theme.muted };
  const pc = spec.point?.curve ?? 0;
  const px = state.point?.x ?? null;

  return (
    <div style={{ opacity: appear, transform: `scale(${0.96 + 0.04 * appear})`, background: theme.figureBg, borderRadius: 22, padding: 18, width, boxSizing: "border-box", border: theme.rough ? `3px solid ${theme.cardBorder}` : "none", ...(theme.rough ? { borderRadius: "255px 15px 225px 15px / 15px 225px 15px 255px" } : {}) }}>
      {label && <div style={{ fontSize: 30, fontWeight: 700, color: theme.rough ? theme.text : "#15213B", marginBottom: 6 }}>{label}
        {spec.sliders.map((s) => <span key={s.name} style={{ fontWeight: 500, fontSize: 26, color: theme.rough ? theme.muted : "#5B6478", marginLeft: 22 }}><i>{s.name}</i> = <b style={{ color: theme.rough ? theme.amber : theme.curve }}>{fmt(Math.round(params[s.name] * 100) / 100)}</b></span>)}
      </div>}
      <style>{`.jg-figure .MafsView { ${Object.entries(theme.mafs).map(([k, v]) => `${k}: ${v};`).join(" ")} font-size: 17px; font-family: ${theme.fontFamily}; }`}</style>
      <div className="jg-figure" style={{ filter: theme.rough ? "url(#chalk)" : undefined }}>
        <Mafs width={width - 36} height={height} viewBox={{ x: spec.view.x, y: spec.view.y }} pan={false} zoom={false} preserveAspectRatio={false}>
          <Coordinates.Cartesian subdivisions={xStep === 1 && yStep === 1 ? 2 : false} xAxis={{ lines: xStep, labels: (v) => fmt(v) }} yAxis={{ lines: yStep, labels: (v) => fmt(v) }} />
          {draw > 0 && spec.curves.map((c, i) => (
            <Plot.Parametric key={i} xy={(t) => [t, evalAt(i, t)]} t={[xMin, xMin + Math.max(0.001, draw) * (xMax - xMin)]} color={colors[c.color] ?? theme.curve} weight={theme.rough ? 4.5 : c.color === "teal" ? 4 : 2.5} style={c.style} />
          ))}
          {state.marks.map((m) => {
            const y = evalAt(pc, m.x);
            const k = clamp01((frame - m.at) / (0.35 * FPS));
            if (!Number.isFinite(y) || k <= 0) return null;
            return (
              <g key={`m${m.x}`} opacity={k}>
                <Point x={m.x} y={y} color={theme.point} />
                <Text x={m.x} y={y} attach="ne" attachDistance={14} size={theme.rough ? 26 : 22} color={theme.rough ? theme.text : "#15213B"}>{m.label || `(${fmt(m.x)} ; ${fmt(y)})`}</Text>
              </g>
            );
          })}
          {px !== null && Number.isFinite(evalAt(pc, px)) && (() => {
            const k = clamp01((frame - state.point!.at) / (0.35 * FPS));
            const y = evalAt(pc, px);
            return (
              <g opacity={k} transform={`scale(${0.6 + 0.4 * k})`} style={{ transformOrigin: "center" }}>
                <Point x={px} y={y} color={theme.point} />
                <Text x={px} y={y} attach="se" attachDistance={14} size={theme.rough ? 26 : 22} color={theme.rough ? theme.amber : "#B86E00"}>{`(${fmt(px)} ; ${fmt(Math.round(y * 100) / 100)})`}</Text>
              </g>
            );
          })()}
        </Mafs>
      </div>
    </div>
  );
}
